import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {Link} from '../../../../libs/shared/ui/src/components/links/link';
import {useLinkNavigation} from '../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {SetupGuideResourceTaskResource} from './setupGuideTypes';

/*
 * Props.
 */

interface SetupGuideResourceTaskProps {
  title: ReactNode;
  image: string;
  resources: ReadonlyArray<SetupGuideResourceTaskResource>;
  onInternalTaskStart: () => void;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  display: flex;
  gap: 12px;
`;

const StyledImage = styled.img`
  width: 146px;
  height: 90px;
`;

const StyledContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  flex: 1 0 0;
`;

const StyledTitleDiv = styled.div`
  padding: 0px 4px;
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
  line-height: 118.75%;
`;

const StyledResourcesContainerDiv = styled.div`
  width: 100%;
  display: grid;
  grid-auto-flow: column;
  row-gap: 0;
  column-gap: 16px;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
`;

const StyledResourceDiv = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px;
  border-radius: 8px;
  gap: 8px;

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledResourceIcon = styled(Icon)`
  width: 16px;
  height: 16px;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledExternalLinkIcon = styled(StyledResourceIcon)`
  display: none;

  ${StyledResourceDiv}:hover & {
    display: block;
  }
`;

const StyledResourceLink = styled(Link)`
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.large};
  line-height: 118.75%;
`;

/*
 * Component.
 */

export const SetupGuideResourceTask: FC<SetupGuideResourceTaskProps> = (props) => {
  const {navigateToLink} = useLinkNavigation();
  const {trackDataEvent} = useDataEventTracking();

  const onClick = (resource: SetupGuideResourceTaskResource) => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_GET_MORE_LMS_COURSE,
      {
        url: resource.link,
        display_name: resource.displayName,
      },
    );

    if (resource.isInternal) {
      navigateToLink(resource.link);
      props.onInternalTaskStart();
      return;
    }

    openInBrowser(resource.link);
  };

  return (
    <StyledContainerDiv>
      <StyledImage src={props.image} />
      <StyledContentDiv>
        <StyledTitleDiv>{props.title}</StyledTitleDiv>
        <StyledResourcesContainerDiv>
          {props.resources.map((resource) => (
            <div key={resource.key}>
              <StyledResourceDiv>
                <StyledResourceIcon name={resource.icon} />
                <StyledResourceLink to={resource.link} onClick={() => onClick(resource)}>
                  {resource.title}
                </StyledResourceLink>
                {!resource.isInternal && <StyledExternalLinkIcon name="externalLinkSmall" />}
              </StyledResourceDiv>
            </div>
          ))}
        </StyledResourcesContainerDiv>
      </StyledContentDiv>
    </StyledContainerDiv>
  );
};
